/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { useTheme, useMergedRefs } from '../utils/index.js';
/**
 * File upload component to be wrapped around `FileUploadTemplate` or any arbitrary component.
 * Provides support for dragging and dropping multiple files.
 * @example
 * <FileUpload onFileDropped={console.log}><FileUploadTemplate /></FileUpload>
 * <FileUpload dragContent='Drop file here' onFileDropped={console.log}><Textarea /></FileUpload>
 */
export const FileUpload = React.forwardRef((props, ref) => {
    const { dragContent, children, onFileDropped, className, ...rest } = props;
    useTheme();
    const [isDragActive, setIsDragActive] = React.useState(false);
    const fileUploadRef = React.useRef(null);
    const refs = useMergedRefs(fileUploadRef, ref);
    const onDragOverHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const onDragEnterHandler = (e) => {
        var _a, _b, _c;
        e.preventDefault();
        e.stopPropagation();
        // only set active if a file is dragged over
        if (!isDragActive && ((_c = (_b = (_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.kind) === 'file') {
            setIsDragActive(true);
        }
    };
    const onDragLeaveHandler = (e) => {
        var _a;
        e.preventDefault();
        e.stopPropagation();
        // only set inactive if secondary target is outside the component
        if (isDragActive &&
            !((_a = fileUploadRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget))) {
            setIsDragActive(false);
        }
    };
    const onDropHandler = (e) => {
        var _a;
        e.preventDefault();
        e.stopPropagation();
        if (isDragActive) {
            setIsDragActive(false);
            onFileDropped((_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.files);
        }
    };
    return (React.createElement("div", { className: cx('iui-file-upload', { 'iui-drag': isDragActive }, className), onDragEnter: onDragEnterHandler, onDragOver: onDragOverHandler, onDragLeave: onDragLeaveHandler, onDrop: onDropHandler, ref: refs, ...rest },
        dragContent ? children : React.createElement("div", { className: 'iui-content' }, children),
        dragContent && React.createElement("div", { className: 'iui-content' }, dragContent)));
});
export default FileUpload;
